import React, { useMemo, useEffect } from "react";
import { Typography } from "antd";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { CHART_COLORS } from "../../../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";

export type RoundStatusFilter = "all" | "qualified" | "unqualified" | "pending";

interface AssessmentStatusFilterProps {
  value: RoundStatusFilter;
  onChange: (value: RoundStatusFilter) => void;
  counts: {
    all: number;
    qualified: number;
    unqualified: number;
    pending: number;
  };
}

const AssessmentStatusFilter: React.FC<AssessmentStatusFilterProps> = ({
  value,
  onChange,
  counts,
}) => {
  // Safeguard: Automatically reset to "all" if current filter has count 0
  // This prevents race conditions when switching between applications
  useEffect(() => {
    if (value !== "all" && counts[value] === 0) {
      onChange("all");
    }
  }, [value, counts, onChange]);
  const chartData = useMemo(
    () =>
      [
        {
          key: "qualified" as RoundStatusFilter,
          label: "Qualified",
          value: counts.qualified,
          color: CHART_COLORS.success,
        },
        {
          key: "unqualified" as RoundStatusFilter,
          label: "Unqualified",
          value: counts.unqualified,
          color: CHART_COLORS.error,
        },
        {
          key: "pending" as RoundStatusFilter,
          label: "Pending",
          value: counts.pending,
          color: CHART_COLORS.warning,
        },
      ].filter((item) => item.value > 0),
    [counts]
  );

  const legendItems = [
    {
      key: "all" as RoundStatusFilter,
      label: "All Attempts",
      value: counts.all,
      color: CHART_COLORS.primary,
    },
    {
      key: "qualified" as RoundStatusFilter,
      label: "Qualified",
      value: counts.qualified,
      color: CHART_COLORS.success,
    },
    {
      key: "unqualified" as RoundStatusFilter,
      label: "Unqualified",
      value: counts.unqualified,
      color: CHART_COLORS.error,
    },
    {
      key: "pending" as RoundStatusFilter,
      label: "Pending",
      value: counts.pending,
      color: CHART_COLORS.warning,
    },
  ];

  const hasData = counts.all > 0;
  const displayChartData = hasData
    ? chartData
    : [
        {
          key: "placeholder" as RoundStatusFilter,
          label: "No Data",
          value: 1,
          color: CHART_COLORS.placeholder,
        },
      ];

  return (
    <div className="assessment-status-filter">
      <div className="assessment-chart-container">
        <div className="assessment-chart-wrapper">
          <ResponsiveContainer width="100%" height={100}>
            <PieChart>
              <Pie
                data={displayChartData}
                cx="50%"
                cy="50%"
                innerRadius={25}
                outerRadius={35}
                paddingAngle={2}
                dataKey="value"
              >
                {displayChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="assessment-chart-center">
            <Typography.Text strong style={{ fontSize: 16 }}>
              {counts.all}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              Total
            </Typography.Text>
          </div>
        </div>
        <div className="assessment-chart-legend">
          {legendItems.map((item) => {
            // Disable if count is 0 (no rounds with this status)
            const isDisabled = item.value === 0;
            
            return (
              <div
                key={item.key}
                className={`assessment-legend-item ${value === item.key ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                onClick={() => {
                  if (!isDisabled) {
                    onChange(item.key);
                  }
                }}
                style={
                  value === item.key && !isDisabled
                    ? {
                        borderColor: item.color,
                        backgroundColor: `${item.color}15`,
                      }
                    : {}
                }
              >
                <span
                  className="assessment-legend-color"
                  style={{ backgroundColor: item.color }}
                />
                <span className="assessment-legend-text">
                  {item.label} ({item.value})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssessmentStatusFilter;



